// Adapt the backend's raw Salesforce-shaped row into the UI Contact type.
// Backend returns rows like:
//   { contact: { Id, FirstName, LastName, INPACT_Associate_Status__c, ... },
//     opportunities: [...] }
// Field names verified 2026-05-05 against live /api/inpact/search response.
// This module is the only place we touch SF field names; the rest of the UI
// reads typed Contact fields.
import type { Certification, Contact, SearchRow } from "../types/inpact";

const CERT_LEVEL_MAP: Record<string, { level: Certification["level"]; name: string }> = {
  "1st Level Certification": { level: "L1", name: "1st Level" },
  "T1D Screening (2nd Level)": { level: "L2", name: "T1D Screening" },
  "Beta Cell Therapy (2nd Level)": { level: "L2", name: "Beta Cell Therapy" },
  "Clinical Trials (2nd Level)": { level: "L2", name: "Clinical Trials" },
  "INNODIA Detect (Project Specific)": { level: "PS", name: "INNODIA Detect" },
};

function pickString(o: Record<string, unknown>, key: string): string | null {
  const v = o[key];
  if (typeof v === "string" && v.length > 0) return v;
  // Multipicklists serialised by the backend as a sorted list (set→list).
  if (Array.isArray(v) && v.length > 0) {
    return v.filter((x) => typeof x === "string").join(", ");
  }
  return null;
}

function pickBool(o: Record<string, unknown>, key: string): boolean {
  return o[key] === true;
}

function parseCertifications(raw: unknown): Certification[] {
  // Live SF returns INPACT_Certification_Types__c as either an array of strings
  // (modern multipicklist serialiser) or a `;`-delimited string (legacy).
  const entries: string[] = Array.isArray(raw)
    ? raw.filter((s): s is string => typeof s === "string")
    : typeof raw === "string"
      ? raw.split(";").map((s) => s.trim()).filter(Boolean)
      : [];
  return entries.map((entry) => {
    const mapped = CERT_LEVEL_MAP[entry];
    return mapped
      ? { level: mapped.level, name: mapped.name, awardedDate: null }
      : { level: "L1" as const, name: entry, awardedDate: null };
  });
}

export function adaptContact(row: SearchRow): Contact {
  const c = row.contact;
  const accountRaw = c.Account as { Name?: string } | undefined;
  const accountId = pickString(c, "AccountId");

  return {
    id: pickString(c, "Id") ?? "",
    firstName: pickString(c, "FirstName") ?? "",
    lastName: pickString(c, "LastName") ?? "",
    email: pickString(c, "Email"),
    countryOfResidence: pickString(c, "Country_of_Residence_PL__c"),
    countryOfBirth: pickString(c, "Country_of_birth_PL__c"),
    associateStatus:
      (pickString(c, "INPACT_Associate_Status__c") as Contact["associateStatus"]) ?? null,
    inactive: pickBool(c, "INPACT_Inactive_Contact__c"),
    countryLead: pickBool(c, "INPACT_Country_Lead__c"),
    leadingTeam: pickBool(c, "INPACT_Leading_Team__c"),
    certifications: parseCertifications(c["INPACT_Certification_Types__c"]),
    account:
      accountId
        ? { id: accountId, name: accountRaw?.Name ?? "" }
        : null,
    pwdStatus: pickString(c, "PwD_Status__c"),
    educationLevel: pickString(c, "Education__c"),
    dateOfApplication: pickString(c, "Date_of_application__c"),
    dateOfApproval: pickString(c, "Date_of_approval__c"),
  };
}
